import { Box, Button, Dialog, DialogActions, DialogContent, Divider, FormControl, Grid, InputLabel, MenuItem, Select, selectGlasses, TextField, Typography } from "@mui/material";
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { addYears } from "date-fns";
import { calculateItemTotal, formatAmountForDisplay, formatAmountForDisplayWithPreferences, getCurrentUTCDate, niceDate } from "../../../../../utils";
import { SelectLocation } from "../../../../common";
import AppDialogTitle from "../../../../ui/appDialogTitle";
import { glassLensModel } from "../../../model/glassesLensModel";
import OrderButtonComponent, { ButtonType } from "./invoiceButtonComponents";
import TreatmentSelectDialog from "../../../../admin/ValidData/validTreatment/treatmentSelectDialog";
import AccessorySelectDialog from "../../../../admin/inventory/accessories/accessorySelectDialog";
import InvoiceItemsTable from "./invoiceItemsTable";
import PaymentCollector from "./paymentCollector";
import DiscountPromotion from "./discountAndPromotion";
import InsuranceClaimCollector from "./insuranceClaimCollector";
import { useQuery } from "react-query";
import { ValidDataService } from "../../../../../service/validDataService";
import InvoiceStatusSelect from "./invoiceStatusSelect";
import { InvoiceService } from "../../../../../service/invoiceService";
import { isLocalDev } from "utils/environment";
import { toast } from "react-toastify";
import CustomerInfobar from "../../../customerInfobar";
import ServiceSelectDialog from "../../../../admin/ValidData/validServices/serviceSelectDialog";
import NewGlassesDialog from "./newGlassesDialog";
import ManualGlassesDialog from "./ManualGlassesDialog";
import ManualLensDialog from "./ManualLensDialog";
import ManualContactLensDialog from "./ManualContactLensDialog";
import NewContactsDialog from "./newContactsDialog";
import ContactLensConfigurationDialog from "./ContactLensConfigurationDialog";
import { v4 as uuidv4 } from 'uuid'
import LabOrderDialog from "../../labOrder/labOrderDialog";
import useInvoiceState from "hooks/useInvoiceState";
import useInvoiceCalculations from "hooks/useInvoiceCalculations";
import FrameSelectDialog from "../../../../admin/inventory/frames/frameSelectDialog";
import { useLocation } from "hooks/useLocation";
import { useEffect } from "react";
import { useAuth } from "hooks/useAuth";
import { usePreferences } from "hooks/usePreferences";
// LensSelectDialog removed - lens configuration is now part of NewGlassesDialog workflow
import AddTaskDialog from "../../../../task/AddTaskDialog";
import {
    buildGlassesInvoiceItems,
    buildUpdatedGlassesInvoiceItems,
    mapGroupItemsToGlassesData,
} from "./glassesOrderMapper";
import { toDateInputValueInTimeZone } from "components/appointments/utils";


export default function AddNewInvoiceDialog({ onClose, open, customer, onRefresh }) {

    const { data: invoiceStatusType, isLoading: invoivceStatusTypeLoading, error: lensTypeError } = useQuery('invoiceStatusTypes', ValidDataService.getInvoiceStatusTypes)

    // Get global selected location
    const { selectedLocation } = useLocation();
    const { preferences } = usePreferences();
    const { user } = useAuth();
    const effectiveTimezone = selectedLocation?.timezone || preferences?.timezone || 'UTC';
    const defaultInvoiceDate = toDateInputValueInTimeZone(new Date(), effectiveTimezone);

const {
        invoiceState: state,
        actions
    } = useInvoiceState({
        id: null,
        invoiceNumber: null,
        selectService: false,
        selectTreatment: false,
        selectAccessory: false,
        selectFrame: false,
        selectLens: false,
        selectGlasses: false,
        selectContacts: false,
        selectConfiguredContactLens: false,
        editGlasses: false,
        selectManualGlasses: false,
        editManualGlasses: false,
        manualFrameToEdit: null,
        selectManualLens: false,
        selectManualContactLens: false,
        editManualLens: false,
        manualLensToEdit: null,
        editManualContactLens: false,
        manualContactLensToEdit: null,
        editContacts: false,
        editFrame: false,
        editingFrameItem: null,
        labOrderDialog: false,
        glassesToEdit: null,
        contactsToEdit: null,
        inProgress: false,
        location: selectedLocation || null,
        notes: '',
        tray: '',
        associate: user ? `${user.firstName} ${user.lastName}` : '',
        invoiceItems: [],
        glassesOrdersForLab: [],
        payments: [],
        discounts: [],
        claims: [],
        refunds: [],
        creditMemos: [],
        status: 'in progress',
        invoiceDate: defaultInvoiceDate,
        endDate: toDateInputValueInTimeZone(addYears(Date.now(), 1), effectiveTimezone),
        tabIndex: 0,
        errors: {},
        showTaskDialog: false,
    });

    // Sync invoice location with global location selector changes
    useEffect(() => {
        if (selectedLocation && (!state.location || String(state.location.id) !== String(selectedLocation.id))) {
            actions.setInvoiceMeta({ location: selectedLocation });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation?.id]);

    // calculations
    const { lineItemsSubtotal, totalDiscount, totalTax, totalDue, totalCollected, balanceDue, paymentStatus, totalItemTaxAmount, totalItemDiscountAmount, totalOverallDiscountAmount,
        taxOnDiscountedAmount,
        totalCustomerPaid,
        totalInsuranceApproved,
        totalRefunded,
        maxRefundAmount } = useInvoiceCalculations(state);
    const hasNonZeroDiscount = Math.abs(totalDiscount || 0) > 0.000001;



    // validate before saving
    const validate = () => {
        const newErrors = {};
        if (!state.location) newErrors.location = 'Location is required';
        if (!state.associate) newErrors.associate = 'Associate is required';
        if (!state.tray) newErrors.tray = 'Tray is required';

        actions.setInvoiceMeta({ type: 'SET_ERRORS', errors: newErrors });
        return Object.keys(newErrors).length === 0;
    }

    const processedInvoiceItemsForSave = state.invoiceItems.map(item => {
        const itemForSave = {
            ...item,
            entryMethod: item.entryMethod || 'catalog'
        }; // Create a shallow copy to avoid modifying original state

        // If this item was newly added in the UI, it will have a client-side 'id'
        // but no database-assigned 'invoice_item_id' (assuming 'invoice_item_id' is your actual DB PK field name).
        if (itemForSave.id && !itemForSave.invoice_item_id) { // This checks if it's a temp UI ID
            delete itemForSave.id; // <-- REMOVE THE CLIENT-SIDE TEMP ID HERE
            // You might also want to explicitly set invoice_item_id to undefined/null
            // if your ORM benefits from it to signal a new record.
            itemForSave.invoice_item_id = undefined; // Or null, if your backend expects it
        }
        
        // Clean up: Remove UI-only fields before sending to backend
        delete itemForSave.inventoryItem; // Large nested object, not needed by backend
        delete itemForSave.rx; // Only send rxId, not the full rx object
        delete itemForSave.frame; // Flat frame snapshot used only for dialog rehydration
        
        // Clean up itemConfig for lenses: keep IDs and snapshot data (names for display)
        if (itemForSave.itemConfig && itemForSave.type === ButtonType.LENS) {
            if (itemForSave.itemConfig?.manualLens) {
                itemForSave.itemConfig = {
                    manualEntry: true,
                    manualLens: { ...itemForSave.itemConfig.manualLens },
                    model: itemForSave.itemConfig.model || null
                };
                return itemForSave;
            }

            const cleanConfig = {
                // IDs for referential integrity
                manufacturerId: itemForSave.itemConfig.manufacturerId,
                lensModelId: itemForSave.itemConfig.lensModelId,
                lensMaterialId: itemForSave.itemConfig.lensMaterialId,
                lensIndexId: itemForSave.itemConfig.lensIndexId,
                treatmentIds: itemForSave.itemConfig.treatmentIds || [],
                
                // Snapshot data for display (no DB lookups needed on read)
                manufacturer: itemForSave.itemConfig.manufacturer ? {
                    id: itemForSave.itemConfig.manufacturer.id,
                    name: itemForSave.itemConfig.manufacturer.name
                } : null,
                model: itemForSave.itemConfig.model ? {
                    id: itemForSave.itemConfig.model.id,
                    modelName: itemForSave.itemConfig.model.modelName,
                    lensType: itemForSave.itemConfig.model.lensType
                } : null,
                material: itemForSave.itemConfig.material ? {
                    id: itemForSave.itemConfig.material.id,
                    name: itemForSave.itemConfig.material.name
                } : null,
                index: itemForSave.itemConfig.index ? {
                    id: itemForSave.itemConfig.index.id,
                    indexValue: itemForSave.itemConfig.index.indexValue
                } : null,
                treatments: (itemForSave.itemConfig.treatments || []).map(t => ({
                    id: t.id,
                    name: t.name,
                    cost: t.cost,
                    listPrice: t.listPrice
                }))
            };
            itemForSave.itemConfig = cleanConfig;
        }
        
        // If it's an existing item (from invoiceData.items), it should already have
        // itemForSave.invoice_item_id populated (and itemForSave.id in UI state should mirror it).
        return itemForSave;
    });

    const handleSave = async (saveAsQuote = false) => {
        if (!validate()) {

            return;
        }

        // Gather all necessary data from state
        const invoiceDataToSave = {
            // If editing, include rowid
            id: state.id, // Pass the original ID if editing
            invoiceNumber: state.invoiceNumber,
            customerId: customer.customer.id, // Assuming this is the customer ID
            associate: state.associate,
            creditMemos: state.creditMemos, // Include credit memos if needed
            tray: state.tray,
            invoiceItems: state.invoiceItems,
            glassesOrdersForLab: state.glassesOrdersForLab || [],
            totalAmount: totalDue,
            paidAmount: totalCustomerPaid,
            balanceAmount: balanceDue,
            taxAmount: totalItemTaxAmount,
            discountAmount: totalOverallDiscountAmount,
            payments: state.payments,
            discounts: state.discounts,
            claims: state.claims,
            location: state.location,
            status: saveAsQuote ? 'quote' : state.status,
            notes: state.notes,
            invoiceDate: state.invoiceDate, // Or from state if added
            // Include formik values if they represent the core RX data for the order
            // rxData: formik.values,
            // Or if RX is an item, ensure item has rxDetails property
        };

        try {
            // Assign the processed items back to the data to be sent
            invoiceDataToSave.invoiceItems = processedInvoiceItemsForSave;
            actions.setInvoiceMeta({ inProgress: true });
            const response = await InvoiceService.save(invoiceDataToSave);
            // if invoice number is not set then we need to create a new invoice
            // if (!state.invoiceNumber) {
            //     response = await InvoiceService.save(invoiceDataToSave);
            // } else {
            //     // if invoice number is set then we need to update the invoice
            //     invoiceDataToSave.id = state.id;
            //     response = await InvoiceService.update(invoiceDataToSave);
            // }
            actions.setInvoiceMeta({ inProgress: false });
            const { code, message, data } = response;
            if (code !== 0) {
                console.error('Error saving invoice:', message);
                toast.error(message);
                return;
            }
            // update invoice number in the state
            actions.setInvoiceMeta({ invoiceNumber: data.invoiceNumber, id: data.id });

            toast.success(message);

            // Refresh parent invoice list if callback provided
            if (onRefresh) {
                onRefresh();
            }

        } catch (error) {
            if (isLocalDev) {
                console.error('Error saving invoice:', error);
            }
        }
    }

    const handleAssociateChange = (event) => {
        const inputValue = event.target.value;
        actions.setInvoiceMeta({ associate: inputValue });
    }

    const handleTrayChange = (event) => {
        const inputValue = event.target.value;
        actions.setInvoiceMeta({ tray: inputValue });
    }

    const handleAddTask = () => {
        actions.setInvoiceMeta({ showTaskDialog: true });
    };

    const handleButtonClick = (buttonType) => {
        switch (buttonType) {
            case ButtonType.NEW_GLASSES:
                actions.setInvoiceMeta({ selectGlasses: true });
                break;
            case ButtonType.MANUAL_GLASSES:
                actions.setInvoiceMeta({ selectManualGlasses: true });
                break;
            case ButtonType.MANUAL_LENS:
                actions.setInvoiceMeta({ selectManualLens: true });
                break;
            case ButtonType.MANUAL_CONTACT_LENS:
                actions.setInvoiceMeta({ selectManualContactLens: true });
                break;
            case ButtonType.CONTACT_LENS:
                actions.setInvoiceMeta({ selectConfiguredContactLens: true });
                break;
            case ButtonType.FRAME:
                actions.setInvoiceMeta({ selectFrame: true });
                break;
            // Note: ButtonType.LENS removed - lens configuration is now part of NEW_GLASSES workflow
            case ButtonType.SERVICES:
                actions.setInvoiceMeta({ selectService: true });
                break;
            case ButtonType.TREATMENTS:
                actions.setInvoiceMeta({ selectTreatment: true });
                break;
            case ButtonType.ACCESSORIES:
                actions.setInvoiceMeta({ selectAccessory: true });
                break;
            case ButtonType.SEND_TO_LAB:
                actions.setInvoiceMeta({ labOrderDialog: true });
                break;                  
            default:
                break;
        }
    }

    const handleEditItem = (item) => {
        const isManualEntry = item?.entryMethod === 'manual';
        const isManualFrame = isManualEntry && item?.type === ButtonType.FRAME;
        const isManualLens = isManualEntry && item?.type === ButtonType.LENS && Boolean(item?.itemConfig?.manualLens);
        const isManualContactLens = isManualEntry && item?.type === ButtonType.CONTACT_LENS && Boolean(item?.itemConfig?.manualContactLens);

        if (isManualFrame) {
            actions.setInvoiceMeta({
                editManualGlasses: true,
                manualFrameToEdit: {
                    item,
                    groupId: item.groupId || null,
                },
            });
            return;
        }

        if (isManualLens) {
            const groupId = item.groupId || null;
            const grouped = groupId
                ? state.invoiceItems.filter((invoiceItem) => invoiceItem.groupId === groupId)
                : [item];
            const odLens = grouped.find((invoiceItem) => invoiceItem.type === ButtonType.LENS && invoiceItem.eye === 'OD') || null;
            const osLens = grouped.find((invoiceItem) => invoiceItem.type === ButtonType.LENS && invoiceItem.eye === 'OS') || null;

            actions.setInvoiceMeta({
                editManualLens: true,
                manualLensToEdit: {
                    groupId,
                    odLens,
                    osLens,
                }
            });
            return;
        }

        if (isManualContactLens) {
            const groupId = item.groupId || null;
            const grouped = groupId
                ? state.invoiceItems.filter((invoiceItem) => invoiceItem.groupId === groupId)
                : [item];
            const odContactLens = grouped.find((invoiceItem) => invoiceItem.type === ButtonType.CONTACT_LENS && invoiceItem.eye === 'OD') || null;
            const osContactLens = grouped.find((invoiceItem) => invoiceItem.type === ButtonType.CONTACT_LENS && invoiceItem.eye === 'OS') || null;

            actions.setInvoiceMeta({
                editManualContactLens: true,
                manualContactLensToEdit: {
                    groupId,
                    odContactLens,
                    osContactLens,
                }
            });
            return;
        }
        
        // show the editglasses dialog only if item type is glasses
        if (item.groupId && (item.type === ButtonType.FRAME || item.type === ButtonType.LENS)) {
            // Find all items with the same groupId
            const groupItems = item.groupId
                ? state.invoiceItems.filter(i => i.groupId === item.groupId)
                : [item];

            const glassesData = mapGroupItemsToGlassesData(groupItems, item.groupId);

            actions.setInvoiceMeta({ editGlasses: true, glassesToEdit: glassesData });
        } else if(item.type === ButtonType.CONTACT_LENS) {
            // Find all items with the same groupId
            const groupItems = item.groupId
                ? state.invoiceItems.filter(i => i.groupId === item.groupId)
                : [item];

            const OD = groupItems.find(i => i.type === 'contact_lens' && i.eye === 'OD');
            const OS = groupItems.find(i => i.type === 'contact_lens' && i.eye === 'OS');
            
            const contactsData = {
                items: groupItems,
                groupId: item.groupId,
                OD,
                OS,
            };

            actions.setInvoiceMeta({ editContacts: true, contactsToEdit: contactsData });            
        
        } else if (item.type === ButtonType.FRAME) {
            // Direct frame item edit - open frame dialog for replacement
            actions.setInvoiceMeta({ editFrame: true, editingFrameItem: item });
        }            
    }

    const handleDeleteItem = (item) => {
        actions.removeItem(item);
    };

    const handleReplaceFrame = (newFrame) => {
        // Replace the existing frame item with the newly selected frame
        if (state.editingFrameItem) {
            const updatedFrameItem = {
                ...state.editingFrameItem,
                itemId: newFrame.id,
                modelNumber: newFrame.modelNumber,
                brand: newFrame.brand || null,
                description: newFrame.name || newFrame.modelNumber || newFrame.description || 'Frame',
                price: +newFrame.listPrice || +newFrame.price || +newFrame.cost || 0,
                tax: newFrame.defaultTaxPercentage || 0,
                total: calculateItemTotal({
                    price: +newFrame.listPrice || +newFrame.price || +newFrame.cost || 0,
                    quantity: state.editingFrameItem.quantity || 1,
                    tax: newFrame.defaultTaxPercentage || 0,
                    discount: state.editingFrameItem.discount || 0
                }),
                properties: newFrame.properties || {}
            };
            actions.updateItem({ item: state.editingFrameItem, updatedItem: updatedFrameItem });
            actions.setInvoiceMeta({ editFrame: false, editingFrameItem: null });
        }
    };

    // Add this handler at the component level
    const handleItemUpdate = (item, field, value) => {
        actions.updateItem({ item, field, value });
    };

    /*
        called after glass order is edited and closed
    */
    const handleUpdateGlasses = (updatedGlasses) => {
        const glassesToEdit = state.glassesToEdit;

        if (glassesToEdit && glassesToEdit.items) {
            const nextItems = buildUpdatedGlassesInvoiceItems(glassesToEdit.items, updatedGlasses);

            if (nextItems.frameItem) {
                const currentFrameItem = glassesToEdit.items.find(item => item.type === 'frame' && item.groupId === updatedGlasses.groupId);
                actions.updateItem({ item: currentFrameItem, updatedItem: nextItems.frameItem });
            }

            if (nextItems.odLensItem) {
                const currentODLensItem = glassesToEdit.items.find(item => item.type === 'lens' && item.eye === 'OD' && item.groupId === updatedGlasses.groupId);
                if (currentODLensItem) {
                    actions.updateItem({ item: currentODLensItem, updatedItem: nextItems.odLensItem });
                } else {
                    actions.addItem(nextItems.odLensItem);
                }
            }

            if (nextItems.osLensItem) {
                const currentOSLensItem = glassesToEdit.items.find(item => item.type === 'lens' && item.eye === 'OS' && item.groupId === updatedGlasses.groupId);
                if (currentOSLensItem) {
                    actions.updateItem({ item: currentOSLensItem, updatedItem: nextItems.osLensItem });
                } else {
                    actions.addItem(nextItems.osLensItem);
                }
            }

            const existingTreatments = glassesToEdit.items.filter(item => 
                item.type === ButtonType.TREATMENTS && item.groupId === updatedGlasses.groupId
            );
            existingTreatments.forEach(treatment => {
                actions.setInvoiceMeta({ type: 'REMOVE_ITEM', item: treatment });
            });
            nextItems.treatmentItems.forEach((treatmentItem) => {
                actions.addItem(treatmentItem);
            });

            actions.setInvoiceMeta({ editGlasses: false, glassesToEdit: null });
        }
    };

    /*
        called after contacts order is edited and closed
    */
    const handleUpdateContacts = (updatedContacts) => {
        const contactsToEdit = state.contactsToEdit;
        if (contactsToEdit) {
            // Extract OD and OS data from updatedContacts.contactLens
            const od = updatedContacts.od;
            const os = updatedContacts.os;
            const topLevelRx = updatedContacts.rx;

            // Update OD contact lens item
            const odContactItem = contactsToEdit.items.find(item => item.type === ButtonType.CONTACT_LENS && item.eye === 'OD' && item.groupId === updatedContacts.groupId);
            
            if (od) {
                const updatedODItem = {
                    ...odContactItem,
                    itemId: null,
                    description: `Contact Lens (OD): ${od.modelName || 'Contact Lens'}`,
                    quantity: od.boxQuantity || 1,
                    price: od.retailPrice || 0,
                    isTrial: od.isTrial || false,
                    itemConfig: {
                        manufacturerId: od.manufacturerId,
                        contactLensModelId: od.contactLensModelId,
                        scheduleId: od.scheduleId,
                        boxQuantity: od.boxQuantity,
                        cost: od.cost,
                        retailPrice: od.retailPrice,
                        notes: od.notes,
                        modelName: od.modelName,
                        scheduleName: od.scheduleName,
                        daysSupply: od.daysSupply,
                        lensesPerBox: od.lensesPerBox                        
                    },
                    rx: topLevelRx,
                    eye: 'OD',
                    total: calculateItemTotal({
                        ...odContactItem,
                        price: od.retailPrice || 0,
                        quantity: od.boxQuantity || 1
                    })
                };
                actions.updateItem({ item: odContactItem, updatedItem: updatedODItem });
            }

            // Update OS contact lens item
            const osContactItem = contactsToEdit.items.find(item => item.type === ButtonType.CONTACT_LENS && item.eye === 'OS' && item.groupId === updatedContacts.groupId);
            if (os) {
                const updatedOSItem = {
                    ...osContactItem,
                    itemId: null,
                    description: `Contact Lens (OS): ${os.modelName || 'Contact Lens'}`,
                    quantity: os.boxQuantity || 1,
                    price: os.retailPrice || 0,
                    isTrial: os.isTrial || false,
                    itemConfig: {
                        manufacturerId: os.manufacturerId,
                        contactLensModelId: os.contactLensModelId,
                        scheduleId: os.scheduleId,
                        boxQuantity: os.boxQuantity,
                        cost: os.cost,
                        retailPrice: os.retailPrice,
                        notes: os.notes,
                        modelName: os.modelName,
                        scheduleName: os.scheduleName,
                        daysSupply: os.daysSupply,
                        lensesPerBox: os.lensesPerBox                        
                    },
                    rx: topLevelRx,
                    eye: 'OS',
                    total: calculateItemTotal({
                        ...osContactItem,
                        price: os.retailPrice || 0,
                        quantity: os.boxQuantity || 1
                    })
                };
                actions.updateItem({ item: osContactItem, updatedItem: updatedOSItem });
            }

            // No treatments for contacts, but if you ever add, handle here

            actions.setInvoiceMeta({ editContacts: false, contactsToEdit: null });
        }
    }

    const handleUpdateManualFrame = (updatedManualGlasses) => {
        const editingItem = state.manualFrameToEdit?.item;
        if (!editingItem) {
            return;
        }

        const updatedFrameItem = buildGlassesInvoiceItems(updatedManualGlasses).find(
            (invoiceItem) => invoiceItem.type === ButtonType.FRAME
        );

        if (!updatedFrameItem) {
            actions.setInvoiceMeta({ editManualGlasses: false, manualFrameToEdit: null });
            return;
        }

        actions.updateItem({
            item: editingItem,
            updatedItem: {
                ...editingItem,
                ...updatedFrameItem,
                id: editingItem.id,
                groupId: editingItem.groupId,
                entryMethod: 'manual',
            }
        });

        actions.setInvoiceMeta({ editManualGlasses: false, manualFrameToEdit: null });
    };

    const handleUpdateManualLens = (updatedManualLens) => {
        const { odLens, osLens } = updatedManualLens || {};
        const currentOD = state.manualLensToEdit?.odLens || null;
        const currentOS = state.manualLensToEdit?.osLens || null;

        if (currentOD) {
            if (odLens) {
                actions.updateItem({
                    item: currentOD,
                    updatedItem: {
                        ...currentOD,
                        ...odLens,
                        id: currentOD.id,
                        entryMethod: 'manual',
                    }
                });
            } else {
                actions.removeItem(currentOD);
            }
        } else if (odLens) {
            actions.addItem({ ...odLens, entryMethod: 'manual' });
        }

        if (currentOS) {
            if (osLens) {
                actions.updateItem({
                    item: currentOS,
                    updatedItem: {
                        ...currentOS,
                        ...osLens,
                        id: currentOS.id,
                        entryMethod: 'manual',
                    }
                });
            } else {
                actions.removeItem(currentOS);
            }
        } else if (osLens) {
            actions.addItem({ ...osLens, entryMethod: 'manual' });
        }

        actions.setInvoiceMeta({ editManualLens: false, manualLensToEdit: null });
    };

    const handleUpdateManualContactLens = (updatedManualContactLens) => {
        const { odContactLens, osContactLens } = updatedManualContactLens || {};
        const currentOD = state.manualContactLensToEdit?.odContactLens || null;
        const currentOS = state.manualContactLensToEdit?.osContactLens || null;

        if (currentOD) {
            if (odContactLens) {
                actions.updateItem({
                    item: currentOD,
                    updatedItem: {
                        ...currentOD,
                        ...odContactLens,
                        id: currentOD.id,
                        entryMethod: 'manual',
                    }
                });
            } else {
                actions.removeItem(currentOD);
            }
        } else if (odContactLens) {
            actions.addItem({ ...odContactLens, entryMethod: 'manual' });
        }

        if (currentOS) {
            if (osContactLens) {
                actions.updateItem({
                    item: currentOS,
                    updatedItem: {
                        ...currentOS,
                        ...osContactLens,
                        id: currentOS.id,
                        entryMethod: 'manual',
                    }
                });
            } else {
                actions.removeItem(currentOS);
            }
        } else if (osContactLens) {
            actions.addItem({ ...osContactLens, entryMethod: 'manual' });
        }

        actions.setInvoiceMeta({ editManualContactLens: false, manualContactLensToEdit: null });
    };

    const addToInvoice = (type, item) => {
        // handle glasses and contacts items differently
        if (type === ButtonType.NEW_GLASSES || type === ButtonType.MANUAL_GLASSES) {
            const entryMethod = type === ButtonType.MANUAL_GLASSES ? 'manual' : 'catalog';
            buildGlassesInvoiceItems(item).forEach((invoiceItem) => {
                actions.addItem({ ...invoiceItem, entryMethod });
            });

            // Store glasses order for backend processing
            actions.setInvoiceMeta({
                //glassesOrdersForLab: [...(state.glassesOrdersForLab || []), glassesOrder],
                selectService: false,
                selectTreatment: false,
                selectAccessory: false,
                selectGlasses: false,
                selectManualGlasses: false,
                selectManualLens: false,
                selectManualContactLens: false
            });
        } else if (type === ButtonType.MANUAL_LENS) {
            if (item.odLens) {
                actions.addItem({ ...item.odLens, entryMethod: 'manual' });
            }
            if (item.osLens) {
                actions.addItem({ ...item.osLens, entryMethod: 'manual' });
            }
            actions.setInvoiceMeta({ selectManualLens: false });
        } else if (type === ButtonType.MANUAL_CONTACT_LENS) {
            if (item.odContactLens) {
                actions.addItem({ ...item.odContactLens, entryMethod: 'manual' });
            }
            if (item.osContactLens) {
                actions.addItem({ ...item.osContactLens, entryMethod: 'manual' });
            }
            actions.setInvoiceMeta({ selectManualContactLens: false });
        } else if (type === ButtonType.CONTACT_LENS) {
            const { od, os, groupId, rx } = item;
            
            // Add Right Eye (OD) configured contact lens if it exists
            if (od && od.contactLensModelId) {
                const odItem = {
                    id: uuidv4(),
                    itemId: null, // No inventory item for configured contact lenses
                    description: `Contact Lens (OD): ${od.modelName || 'Contact Lens'}`,
                    type: ButtonType.CONTACT_LENS,
                    entryMethod: 'catalog',
                    quantity: od.boxQuantity || 1,
                    price: od.retailPrice || 0,
                    tax: 0, // Tax calculated at invoice level
                    discount: 0,
                    total: 0,
                    date: getCurrentUTCDate(),
                    groupId: groupId,
                    eye: 'OD',
                    rxId: rx?.id || null, // Extract rxId from top-level rx (shared for both eyes)
                    rx: rx, // Store full rx object for editing before save                    
                    isTrial: od.isTrial || false,
                    // Store contact lens configuration metadata
                    itemConfig: {
                        manufacturerId: od.manufacturerId,
                        contactLensModelId: od.contactLensModelId,
                        scheduleId: od.scheduleId,
                        boxQuantity: od.boxQuantity,
                        cost: od.cost,
                        retailPrice: od.retailPrice,
                        notes: od.notes,
                        modelName: od.modelName,
                        scheduleName: od.scheduleName,
                        daysSupply: od.daysSupply,
                        lensesPerBox: od.lensesPerBox
                    }
                };
                actions.addItem(odItem);
            }

            // Add Left Eye (OS) configured contact lens if it exists
            if (os && os.contactLensModelId) {
                const osItem = {
                    id: uuidv4(),
                    itemId: null, // No inventory item for configured contact lenses
                    description: `Contact Lens (OS): ${os.modelName || 'Contact Lens'}`,
                    type: ButtonType.CONTACT_LENS,
                    entryMethod: 'catalog',
                    quantity: os.boxQuantity || 1,
                    price: os.retailPrice || 0,
                    tax: 0, // Tax calculated at invoice level
                    discount: 0,
                    total: 0,
                    date: getCurrentUTCDate(),
                    groupId: groupId,
                    eye: 'OS',
                    rxId: rx?.id || null, // Extract rxId from top-level rx (shared for both eyes)
                    rx: rx, // Store full rx object for editing before save                    
                    isTrial: os.isTrial || false,
                    // Store contact lens configuration metadata
                    itemConfig: {
                        manufacturerId: os.manufacturerId,
                        contactLensModelId: os.contactLensModelId,
                        scheduleId: os.scheduleId,
                        boxQuantity: os.boxQuantity,
                        cost: os.cost,
                        retailPrice: os.retailPrice,
                        notes: os.notes,
                        modelName: os.modelName,
                        scheduleName: os.scheduleName,
                        daysSupply: os.daysSupply,
                        lensesPerBox: os.lensesPerBox
                    }
                };
                actions.addItem(osItem);
            }

            // Close the dialog after adding items
            actions.setInvoiceMeta({
                selectConfiguredContactLens: false
            });
        } else if (type === ButtonType.FRAME) {
            
            // Add frame to invoice
            const frameItem = {
                id: uuidv4(), // Generate a unique ID for this instance in the UI state
                itemId: item.itemId || null, // Inventory item ID (null for patient own frames)
                modelNumber: item.modelNumber || null,
                brand: item.brand || null,
                description:  item.modelNumber,
                type: ButtonType.FRAME,
                entryMethod: 'catalog',
                quantity: item.quantity || 1,
                price: +item.listPrice || +item.price || +item.cost || +item.total || 0,
                tax: item.defaultTaxPercentage || 0,
                discount: item.discount || 0,
                total: 0,
                date: getCurrentUTCDate(),
                groupId: null,
                frameHandling: 'stock',
                properties:  item.properties
            };
            actions.addItem(frameItem);
        } else {
            // take description from name or modelNumber
            const newItem = {
                // Assign a unique ID for this instance in the UI state. Generate a temporary UUID.            
                id: uuidv4(),
                itemId: item.id,
                description: item.name || item.modelNumber || item.description || 'New Item', // Use description from item if available
                type: type,
                quantity: item.quantity || 1,
                price: +item.listPrice || +item.price || +item.cost || +item.total || 0,
                tax: item.defaultTaxPercentage || 0,
                discount: item.discount || 0,
                total: 0,
                date: getCurrentUTCDate(),
            };

            // Dispatch an action to ADD_ITEM
            actions.addItem(newItem );
            actions.setInvoiceMeta({ selectService: false, selectTreatment: false, selectAccessory: false, selectGlasses: false });
        }
    }

    const handleTypeClick = (type) => {
        switch (type) {
            case ButtonType.FRAME:
                actions.setInvoiceMeta({ selectFrame: true });
                break;
            case ButtonType.NEW_GLASSES:
                actions.setInvoiceMeta({ selectGlasses: true });
                break;
            case ButtonType.MANUAL_GLASSES:
                actions.setInvoiceMeta({ selectManualGlasses: true });
                break;
            case ButtonType.MANUAL_LENS:
                actions.setInvoiceMeta({ selectManualLens: true });
                break;
            case ButtonType.MANUAL_CONTACT_LENS:
                actions.setInvoiceMeta({ selectManualContactLens: true });
                break;
            case ButtonType.SERVICES:
                actions.setInvoiceMeta({ selectService: true });
                break;
            case ButtonType.TREATMENTS:
                actions.setInvoiceMeta({ selectTreatment: true });
                break;
            case ButtonType.ACCESSORIES:
                actions.setInvoiceMeta({ selectAccessory: true });
                break;
            // Note: ButtonType.LENS removed - lens configuration is now part of NEW_GLASSES workflow
            default:
                break;
        }
    };

    // --- Summary Calculations ---

    // This represents the sum of the base prices of all items before any item-level taxes or discounts are aggregated.
    //const lineItemsSubtotal = useMemo(() => sumMoney(state.invoiceItems.map(item => (item.quantity || 0) * (item.price || 0))), [state.invoiceItems]); // Recalculate when invoiceItems change

    //const totalItemTaxAmount = useMemo(() => sumMoney(state.invoiceItems.map(calculateItemTaxAmount)), [state.invoiceItems]); // Recalculate when invoiceItems change

    //const totalItemDiscountAmount = useMemo(() => sumMoney(state.invoiceItems.map(calculateItemDiscountAmount)), [state.invoiceItems]); // Recalculate when invoiceItems change

    // Calculate Total Overall Discount Amount from Discounts/Promotions
    //const totalOverallDiscountAmount = useMemo(() => { return calculateTotalOverallDiscountAmount(state.discounts, lineItemsSubtotal);
    //}, [state.discounts, lineItemsSubtotal]); // Recalculate when discounts or lineItemsSubtotal change

    // Calculate Total Discount (Item Discounts + Overall Discounts)
    //const totalDiscount = totalItemDiscountAmount + totalOverallDiscountAmount;

    // Calculate Discounted Subtotal (apply all discounts before tax)
    //const discountedSubtotal = lineItemsSubtotal - totalDiscount;

    // Calculate Tax on Discounted Amount
    /*
    const taxOnDiscountedAmount = useMemo(() => {
        return sumMoney(state.invoiceItems.map(item => {
            const itemSubtotal = (item.quantity || 1) * (item.price || 0);
            const itemDiscountAmount = (item.discount || 0) / 100 * itemSubtotal;
            const itemShare = itemSubtotal > 0 ? itemSubtotal / lineItemsSubtotal : 0;
            const itemOverallDiscount = totalOverallDiscountAmount * itemShare;
            const itemDiscountedAmount = itemSubtotal - itemDiscountAmount - itemOverallDiscount;
            return (item.tax || 0) / 100 * Math.max(0, itemDiscountedAmount);
        }));
    }, [state.invoiceItems, lineItemsSubtotal, totalOverallDiscountAmount]); */

    // Calculate Total Due (Discounted Subtotal + Tax on Discounted Amount)
    //const totalDue = useMemo(() => { return discountedSubtotal + taxOnDiscountedAmount;
    //}, [discountedSubtotal, taxOnDiscountedAmount]);

    // Calculate Total Customer Paid (from payments)
    //const totalCustomerPaid = useMemo(() => sumMoney(state.payments.map(payment => payment.paymentAmount || 0)), [state.payments]); // Recalculate when payments change

    // Calculate Total Insurance Approved (from claims with Approved/Paid status)
    /*
    const totalInsuranceApproved = useMemo(() => {
        return state.claims.reduce((sum, claim) => {
            // Only sum approved or paid claims
            if (claim.status === 'Approved' || claim.status === 'Paid') {
                return sum + (claim.approvedAmount || 0);
            }
            return sum;
        }, 0);
    }, [state.claims]); */ // Recalculate when claims change


    // Calculate Total Collected (Customer Payments + Insurance Approved)
    //const totalCollected = totalCustomerPaid + totalInsuranceApproved;

    // Calculate Balance Due (Total Due - Total Collected)
    //const balanceDue = truncateToTwoDecimals(totalDue) - truncateToTwoDecimals(totalCollected)

    // --- End Summary Calculations ---

    return (
        <Dialog 
            onClose={onClose} 
            open={open} 
            fullScreen 
            maxWidth="xl" 
            fullWidth
            disableScrollLock
            scroll="paper">
            <AppDialogTitle onClose={onClose} title={`New ${state.status === 'quote' ? 'Quote' : 'Invoice'}`} />

            <DialogContent
                sx={{
                    // Critical: Explicitly enable vertical scrolling to fix Safari scroll issues
                    // Safari sometimes fails to recalculate scroll dimensions when async content loads
                    overflowY: 'auto',
                    
                    // Enable momentum/smooth scrolling on iOS and Safari
                    WebkitOverflowScrolling: 'touch',
                    
                    // Establish positioning context for the scroll container
                    position: 'relative',
                    
                    // Custom scrollbar styling for better visibility
                    '&::-webkit-scrollbar': {
                        width: 8,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.3)',
                        borderRadius: 4,
                    },
                }}          
            >
                <Box display="flex" alignItems="baseline" sx={{ p: 1, borderBottom: '1px solid #ccc', backgroundColor: '#f9f9f9', pb: 1 }}>
                    <CustomerInfobar customerData={customer} />
                </Box>

                <Box display={'flex'} alignItems="center" justifyContent='space-between' sx={{ mb: 1, mt: 1 }}>

                    <Box>
                        <Typography sx={{ pr: 1 }}>Invoice date: {niceDate(state.invoiceDate)}</Typography>
                        {
                            state.invoiceNumber && (<Typography sx={{ pr: 2, fontWeight: 'bold' }}>Invoice #: {state.invoiceNumber}</Typography>
                            )
                        }
                    </Box>

                    <Box display={'flex'} alignItems="center">
                        <TextField label="Tray #" labelid="tray" size="small" sx={{ pr: 1 }} value={state.tray}
                            onChange={handleTrayChange} error={!!state.errors.tray} inputProps={{ style: { textTransform: 'uppercase' } }}
                            helperText={state.errors.tray} ></TextField>
                        <TextField label="Associate" labelid="associate" size="small" sx={{ pr: 1 }}
                            value={state.associate} error={!!state.errors.associate}
                            helperText={state.errors.associate} onChange={handleAssociateChange}></TextField>
                        <SelectLocation location={state.location ? state.location.id : ''} setLocation={(location) => actions.setInvoiceMeta({ location: location })} error={!!state.errors.location} helperText={state.errors.location} />
                    </Box>

                </Box>

                {/* buttons for new order, treatment, etc */}
                <OrderButtonComponent
                    onButtonClick={handleButtonClick}
                />
                <Divider sx={{ mt: 1 }} />
                {/* <Button
                    variant="outlined"
                    startIcon={<AttachMoney />} // Or a similar icon like PriceChange
                    onClick={() => {}}
                    sx={{ ml: 1 }} // Add some margin
                >
                    Apply Tax to All Taxable
                </Button> */}
                {/* table to show invoice items */}
                <InvoiceItemsTable
                    invoiceItems={state.invoiceItems}
                    onTypeClick={handleTypeClick}
                    onEditItem={handleEditItem}
                    onDeleteItem={handleDeleteItem}
                    onItemUpdate={handleItemUpdate}
                />
                {/* a grid to show payment and invoice summary like total, tax, etc */}
                <Grid container spacing={1} sx={{ mt: 0.5 }}>

                    <Grid item xs={12} md={6}>
                        {/* Discounts & Promotions Component */}
                        <DiscountPromotion
                            discounts={state.discounts}
                            orderSubtotal={lineItemsSubtotal} // Pass subtotal for calculation display
                            onAddDiscount={(discount) => actions.addDiscount(discount)}
                            onEditDiscount={(discount) => actions.updateDiscount(discount)}
                            onDeleteDiscount={(discountId) => actions.removeDiscount(discountId)}
                        />
                        <InsuranceClaimCollector
                            claims={state.claims}
                            customer={customer}
                            amountToClaim={totalDue} // Pass the total due amount for claim calculation
                            onAddClaim={(claim) => actions.addClaim( claim )}
                            onEditClaim={(claim) => actions.updateClaim( claim )}
                            onDeleteClaim={(claimId) => actions.removeClaim( claimId )}
                        />
                        <PaymentCollector
                            payments={state.payments}
                            onAddPayment={(payment) => actions.addPayment( payment )}
                            onEditPayment={(payment) => actions.updatePayment( payment )}
                            onDeletePayment={(paymentId) => actions.removePayment( paymentId )}
                            customerId={customer.customer.id}
                            invoiceBalance={balanceDue}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} mt={1}>
                        <TextField
                            value={state.notes}
                            onChange={(e) => actions.setInvoiceMeta({ notes: e.target.value })}
                            name="notes"
                            label="Notes"
                            multiline
                            rows={4}
                            fullWidth
                        />
                        <Box sx={{ mt: 1, p: 1, border: '1px solid #ccc', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h6" gutterBottom>Invoice Summary</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Subtotal:</Typography>
                                <Typography>{formatAmountForDisplayWithPreferences(lineItemsSubtotal, preferences)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Discount:</Typography>
                                <Typography>{hasNonZeroDiscount ? '-' : ''}{formatAmountForDisplayWithPreferences(totalDiscount, preferences)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Tax:</Typography>
                                <Typography>{formatAmountForDisplayWithPreferences(taxOnDiscountedAmount, preferences)}</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="h6">Total Due:</Typography>
                                <Typography variant="h6">{formatAmountForDisplayWithPreferences(totalDue, preferences)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Total Customer Paid:</Typography> {/* Renamed label */}
                                <Typography>{formatAmountForDisplayWithPreferences(totalCustomerPaid, preferences)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Total Insurance Approved:</Typography> {/* New label */}
                                <Typography>{formatAmountForDisplayWithPreferences(totalInsuranceApproved, preferences)}</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Balance:</Typography>
                                {/* Highlight Balance */}
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: balanceDue > 0 ? 'error.main' : (balanceDue < 0 ? 'success.main' : 'text.primary')
                                    }}
                                >
                                    {formatAmountForDisplayWithPreferences(balanceDue, preferences)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent >
            <DialogActions sx={{ p: 2, boxShadow: 8 }}>
                {
                    (state.errors.tray || state.errors.location || state.errors.associate) && <Typography color="error" sx={{ pr: 1 }}>Please fix issues before submitting</Typography>
                }
                {/* Status Dropdown */}
                <InvoiceStatusSelect statusTypes={invoiceStatusType} isLoading={invoivceStatusTypeLoading} selectedStatus={state.status} onStatusChange={(newStatus) => actions.setInvoiceMeta({ status: newStatus })} excludedStatuses={['refund']} />
                {
                    state.invoiceItems.length > 0 && (
                        state.invoiceNumber ?
                            <Button color="primary" variant="contained" type="button" onClick={onClose}>Close</Button>
                            : (
                                <>
                                    <Button sx={{ ml: 1 }} color="primary" variant="contained" type="button" onClick={async () => {
                                        if (!validate()) return
                                        await handleSave()
                                        onClose()
                                    }} autoFocus>
                                        Save & Close
                                    </Button>
                                    <Button sx={{ ml: 1 }} color="primary" variant="outlined" type="button" onClick={() => handleSave(false)}>
                                        Save
                                    </Button>
                                    <Button sx={{ ml: 1 }} color="secondary" variant="outlined" type="button" onClick={async () => {
                                        if (!validate()) return
                                        await handleSave(true)
                                        onClose()
                                    }}>
                                        Save as Quote
                                    </Button>
                                </>
                            )
                    )
                }
                {state.invoiceNumber && (
                <Button
                    onClick={handleAddTask}
                    color="info"
                    variant="outlined"
                    startIcon={<PlaylistAddCheckIcon />}
                >
                    Add Task
                </Button>
                )}
                <Button color='cancel' variant="outlined" onClick={onClose}>Cancel</Button>
            </DialogActions>

            <NewGlassesDialog open={state.selectGlasses} onClose={() => actions.setInvoiceMeta({ selectGlasses: false })} onSelect={(glasses) => addToInvoice(ButtonType.NEW_GLASSES, glasses)} customer={customer} location={state.location} />
            <ManualGlassesDialog
                open={state.selectManualGlasses}
                onClose={() => actions.setInvoiceMeta({ selectManualGlasses: false })}
                onSelect={(manualFrameGlasses) => addToInvoice(ButtonType.MANUAL_GLASSES, manualFrameGlasses)}
                location={state.location}
            />
            <ManualLensDialog
                open={state.selectManualLens}
                onClose={() => actions.setInvoiceMeta({ selectManualLens: false })}
                onSelect={(manualLensData) => addToInvoice(ButtonType.MANUAL_LENS, manualLensData)}
            />
            <ManualContactLensDialog
                open={state.selectManualContactLens}
                onClose={() => actions.setInvoiceMeta({ selectManualContactLens: false })}
                onSelect={(manualContactLensData) => addToInvoice(ButtonType.MANUAL_CONTACT_LENS, manualContactLensData)}
            />
            {/* Use NewGlassesDialog for both add and edit - it supports editing via existingGlassesData prop */}
            <NewGlassesDialog
                open={state.editGlasses}
                onClose={() => {
                    actions.setInvoiceMeta({ selectGlasses: false, editGlasses: false, glassesToEdit: null });
                }}
                onSelect={handleUpdateGlasses}
                customer={customer}
                existingGlassesData={state.glassesToEdit}
                location={state.location}
            />
            <ManualGlassesDialog
                open={state.editManualGlasses}
                onClose={() => actions.setInvoiceMeta({ editManualGlasses: false, manualFrameToEdit: null })}
                onSelect={handleUpdateManualFrame}
                location={state.location}
                existingManualFrameData={state.manualFrameToEdit}
            />
            <ManualLensDialog
                open={state.editManualLens}
                onClose={() => actions.setInvoiceMeta({ editManualLens: false, manualLensToEdit: null })}
                onSelect={handleUpdateManualLens}
                existingManualLensData={state.manualLensToEdit}
            />
            <ManualContactLensDialog
                open={state.editManualContactLens}
                onClose={() => actions.setInvoiceMeta({ editManualContactLens: false, manualContactLensToEdit: null })}
                onSelect={handleUpdateManualContactLens}
                existingManualContactLensData={state.manualContactLensToEdit}
            />
            <LabOrderDialog
                open={state.labOrderDialog}
                onClose={() => actions.setInvoiceMeta({ labOrderDialog: false })}
                onSave={(labOrderData) => {
                    console.log('Lab order saved:', labOrderData);
                    // Handle lab order save logic here
                    actions.setInvoiceMeta({ labOrderDialog: false });
                }}
            />              
            <NewContactsDialog open={state.selectContacts} onClose={() => actions.setInvoiceMeta({ selectContacts: false })} onSelect={(contacts) => addToInvoice(ButtonType.NEW_CONTACTS, contacts)} customer={customer} />
            {/* new contact lens configuration dialog */}
            <ContactLensConfigurationDialog 
                open={state.selectConfiguredContactLens} 
                onClose={() => actions.setInvoiceMeta({ selectConfiguredContactLens: false })} 
                customer={customer}
                onSave={(contactLensData) => {
                    // Add groupId to the data
                    const dataWithGroupId = {
                        ...contactLensData,
                        groupId: uuidv4()
                    };
                    addToInvoice(ButtonType.CONTACT_LENS, dataWithGroupId);
                }} 
            />
            {/* edit contact lens configuration dialog */}
            <ContactLensConfigurationDialog 
                open={state.editContacts} 
                onClose={() => actions.setInvoiceMeta({ editContacts: false })} 
                customer={customer}
                onSave={handleUpdateContacts} 
                existingContactLensData={state.contactsToEdit}
            />            
            <ServiceSelectDialog open={state.selectService} onClose={() => actions.setInvoiceMeta({ selectService: false })} onSelect={(service) => addToInvoice(ButtonType.SERVICES, service)} />
            <TreatmentSelectDialog open={state.selectTreatment} onClose={() => actions.setInvoiceMeta({ selectTreatment: false })} onSelect={(treatment) => addToInvoice(ButtonType.TREATMENTS, treatment)} />
            <AccessorySelectDialog open={state.selectAccessory} onClose={() => actions.setInvoiceMeta({ selectAccessory: false })} onSelect={(accessory) => addToInvoice(ButtonType.ACCESSORIES, accessory)} />
            <FrameSelectDialog open={state.selectFrame} onClose={() => actions.setInvoiceMeta({ selectFrame: false })} onSelect={(frame) => addToInvoice(ButtonType.FRAME, frame)} />
            <FrameSelectDialog open={state.editFrame} onClose={() => actions.setInvoiceMeta({ editFrame: false, editingFrameItem: null })} onSelect={handleReplaceFrame} />
            {/* LensSelectDialog removed - lens configuration is now part of NewGlassesDialog workflow */}
            <AddTaskDialog
                open={state.showTaskDialog}
                onClose={() => actions.setInvoiceMeta({ showTaskDialog: false })}
                entityType="invoice"
                entityId={state.id}
                onTaskCreated={() => actions.setInvoiceMeta({ showTaskDialog: false })}
            />  
            
        </Dialog >
    )
}